import { useMemo, useState } from 'react';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Choice,
  EmptyState,
  Icon,
  ItemGroup,
  Row,
  Section,
} from '@efira/ui';

import { useGifts } from '../../api/queries';
import { coins as coinsWord } from '../../bindings/labels';
import { useConsoleStream } from '../stream';

/**
 * Гифтеры: два режима одной секции.
 *
 * «СЕЙЧАС» И «ЗА ВСЁ ВРЕМЯ» - РАЗНЫЕ ВОПРОСЫ. Первый: кто только что
 * отправил, чтобы успеть поблагодарить. Второй: кто держит эфир деньгами.
 * Один список на оба вопроса отвечал бы плохо на оба.
 *
 * ТРИ ЗАГЛУШКИ, А НЕ ОДНА. Без эфира, пустой «сейчас» и пустой «за всё
 * время» требуют разного: выбрать игру, подождать, и «подарков ещё не было
 * вовсе». «Нет данных» на все три случая не говорит ничего.
 */
type Mode = 'live' | 'top';

export function Gifters() {
  const stream = useConsoleStream();
  const gifts = useGifts();
  const [mode, setMode] = useState<Mode>('live');

  /** Топ: сумма монет по зрителю, по убыванию. */
  const top = useMemo(() => {
    const sum = new Map<string, { coins: number; count: number }>();

    for (const gift of stream.gifts) {
      const current = sum.get(gift.actorId) ?? { coins: 0, count: 0 };
      sum.set(gift.actorId, {
        coins: current.coins + (gift.coins ?? 0) * (gift.count ?? 1),
        count: current.count + (gift.count ?? 1),
      });
    }

    return [...sum.entries()]
      .map(([actorId, value]) => ({ actorId, ...value }))
      .sort((a, b) => b.coins - a.coins)
      .slice(0, 20);
  }, [stream.gifts]);

  const live = [...stream.gifts].reverse();
  const giftName = (id: string | undefined) =>
    gifts.data?.gifts.find((item) => item.id === id)?.name ?? 'подарок';

  return (
    <div data-right="gifters-section" className="flex min-h-0 flex-1 flex-col">
      <Section
        title="Гифтеры"
        id="gifters"
        fill
        bodyData={{ 'data-right': 'gifters-feed', 'data-console-scroll': 'gifters' }}
        toggleData={{ 'data-right': 'gifters-toggle' }}
        actions={
          <Choice
            label="Что показывать"
            data-right="gifters-switch"
            className="shrink-0"
            size="sm"
            value={mode}
            onChange={setMode}
            options={[
              { value: 'live', label: 'Сейчас', data: { 'data-right-gifters-mode': 'live' } },
              { value: 'top', label: 'Лучшие', data: { 'data-right-gifters-mode': 'top' } },
            ]}
          />
        }
      >
        {/* Ветки «эфира нет» здесь больше нет: без сессии колонка целиком
            показывает ОДНО состояние (`RightColumn`, находка аудита 11). */}
        {mode === 'live' && live.length === 0 ? (
          <EmptyState id="gifters-empty" icon="gift" title="Подарков пока нет">
            Первый появится здесь в ту же секунду, как его отправят.
          </EmptyState>
        ) : mode === 'top' && top.length === 0 ? (
          <EmptyState id="gifters-empty" icon="trophy" title="Лучшие появятся с подарками">
            Здесь будет видно, кто и на сколько поддержал эфир.
          </EmptyState>
        ) : mode === 'live' ? (
          <ItemGroup>
            {live.map((gift) => (
              <Row
                key={gift.id}
                role="listitem"
                size="sm"
                data-right-gift={gift.id}
                media={
                  <Avatar>
                    <AvatarImage src={stream.members[gift.actorId]?.avatarUrl} alt="" />
                    <AvatarFallback>
                      <Icon name="gift" size="sm" tone="muted" />
                    </AvatarFallback>
                  </Avatar>
                }
                title={stream.members[gift.actorId]?.name ?? 'Зритель'}
                actions={
                  <>
                    <span className="hidden @[260px]:inline">
                      {giftName(gift.giftId)}
                      {gift.count !== undefined && gift.count > 1 ? ` ×${gift.count}` : ''}
                    </span>
                    {/* Монеты - значком: это то, ради чего смотрят в список. */}
                    <Badge variant="secondary">{(gift.coins ?? 0) * (gift.count ?? 1)}</Badge>
                  </>
                }
              />
            ))}
          </ItemGroup>
        ) : (
          <ItemGroup>
            {top.map((row, index) => (
              <Row
                key={row.actorId}
                role="listitem"
                size="sm"
                data-right-gifter-top={row.actorId}
                media={<Badge variant="outline">{index + 1}</Badge>}
                title={stream.members[row.actorId]?.name ?? 'Зритель'}
                actions={<Badge variant="secondary">{coinsWord(row.coins)}</Badge>}
              />
            ))}
          </ItemGroup>
        )}
      </Section>
    </div>
  );
}
